/**
 * DEV-ONLY placeholder textures. Procedurally draws labelled 32x48 character sheets with distinct
 * front / side / back silhouettes and a 4-frame walk cycle, plus 16x16 emote icons and 32x32 grid
 * tiles for the showcase. Frame names follow the atlas convention in README.md so the real art can
 * replace these textures without code changes. Placeholders live under their own keys
 * (`dev-proc-<actorKey>`, `dev-emote-<name>`), never under a production key, so a chapter group can
 * still load the real atlas later. Never ship these: final art comes from assets-src/.
 */
import type Phaser from 'phaser';
import type { ActorAnimState } from '@/core/types/contracts';
import type { Facing } from '@/core/types';
import { getActorDef } from './actorDefs';
import { ACTOR_STATES, FACINGS, frameName } from './anims';
import { EMOTE_ALIAS, EMOTE_ICONS } from './emotes';

export const DEV_FRAME_W = 32;
export const DEV_FRAME_H = 48;
/** Frames generated per state (columns of the sheet). */
export const DEV_FRAMES: Record<ActorAnimState, number> = { idle: 2, walk: 4, talk: 2, sit: 1, write: 2, emote: 1 };

// 3x5 pixel font for dev labels (A-Z, 0-9, ! ? .)
const FONT: Record<string, string[]> = {
  A: ['010', '101', '111', '101', '101'], B: ['110', '101', '110', '101', '110'], C: ['011', '100', '100', '100', '011'],
  D: ['110', '101', '101', '101', '110'], E: ['111', '100', '110', '100', '111'], F: ['111', '100', '110', '100', '100'],
  G: ['011', '100', '101', '101', '011'], H: ['101', '101', '111', '101', '101'], I: ['111', '010', '010', '010', '111'],
  J: ['001', '001', '001', '101', '010'], K: ['101', '101', '110', '101', '101'], L: ['100', '100', '100', '100', '111'],
  M: ['101', '111', '111', '101', '101'], N: ['110', '101', '101', '101', '101'], O: ['010', '101', '101', '101', '010'],
  P: ['110', '101', '110', '100', '100'], Q: ['010', '101', '101', '011', '001'], R: ['110', '101', '110', '101', '101'],
  S: ['011', '100', '010', '001', '110'], T: ['111', '010', '010', '010', '010'], U: ['101', '101', '101', '101', '011'],
  V: ['101', '101', '101', '101', '010'], W: ['101', '101', '111', '111', '101'], X: ['101', '101', '010', '101', '101'],
  Y: ['101', '101', '010', '010', '010'], Z: ['111', '001', '010', '100', '111'],
  '0': ['010', '101', '101', '101', '010'], '1': ['010', '110', '010', '010', '111'], '2': ['110', '001', '010', '100', '111'],
  '3': ['110', '001', '010', '001', '110'], '4': ['101', '101', '111', '001', '001'], '5': ['111', '100', '110', '001', '110'],
  '6': ['011', '100', '110', '101', '010'], '7': ['111', '001', '010', '010', '010'], '8': ['010', '101', '010', '101', '010'],
  '9': ['010', '101', '011', '001', '110'], '!': ['010', '010', '010', '000', '010'], '?': ['110', '001', '010', '000', '010'],
  '.': ['000', '000', '000', '000', '010'],
};

function hex(c: number): string { return `#${c.toString(16).padStart(6, '0')}`; }
function darken(c: number, f = 0.6): number {
  const r = Math.floor(((c >> 16) & 255) * f), g = Math.floor(((c >> 8) & 255) * f), b = Math.floor((c & 255) * f);
  return (r << 16) | (g << 8) | b;
}

function drawLabel(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, color: string): void {
  ctx.fillStyle = color;
  let cx = x;
  for (const ch of text.toUpperCase()) {
    const g = FONT[ch];
    if (g) for (let r = 0; r < 5; r++) for (let c = 0; c < 3; c++) if (g[r]?.[c] === '1') ctx.fillRect(cx + c, y + r, 1, 1);
    cx += 4;
  }
}

interface Pose { legOffset: number; bob: number; armsUp: boolean; mouthOpen: boolean; sitting: boolean; writing: number }

function poseFor(state: ActorAnimState, n: number): Pose {
  const p: Pose = { legOffset: 0, bob: 0, armsUp: false, mouthOpen: false, sitting: false, writing: -1 };
  switch (state) {
    case 'walk': p.legOffset = [1, 0, -1, 0][n % 4] as number; p.bob = n % 2 === 1 ? 1 : 0; break;
    case 'idle': p.bob = n === 1 ? 1 : 0; break;
    case 'talk': p.mouthOpen = n === 1; break;
    case 'sit': p.sitting = true; break;
    case 'write': p.writing = n; break;
    case 'emote': p.armsUp = true; break;
  }
  return p;
}

/** Draws one 32x48 frame at (ox, oy). Feet baseline is the bottom row (y = 47). */
function drawCharacter(ctx: CanvasRenderingContext2D, ox: number, oy: number, facing: Facing, pose: Pose, pal: { robe: number; accent: number; skin: number; hair: number; label: string }): void {
  const robe = hex(pal.robe), robeDark = hex(darken(pal.robe)), skin = hex(pal.skin), hair = hex(pal.hair), accent = hex(pal.accent);
  const outline = hex(darken(pal.robe, 0.35));
  const cx = ox + 16;
  const bob = pose.bob;
  const sit = pose.sitting ? 8 : 0;
  const top = oy + 6 + bob + sit; // head top
  const r = (x: number, y: number, w: number, h: number, c: string) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };

  // legs (skip when sitting: folded)
  if (!pose.sitting) {
    const lo = pose.legOffset;
    r(cx - 6, oy + 38, 5, 9 - Math.max(0, lo), robeDark);
    r(cx + 1, oy + 38, 5, 9 - Math.max(0, -lo), robeDark);
    r(cx - 7, oy + 46 - Math.max(0, lo), 6, 2, outline); // feet
    r(cx + 1, oy + 46 - Math.max(0, -lo), 6, 2, outline);
    if (facing === 'left' || facing === 'right') { // side view: legs closer together, one forward
      r(cx - 4, oy + 38, 4, 9, robeDark);
      r(cx, oy + 38, 4, 9, robeDark);
      if (lo !== 0) r(cx + (facing === 'right' ? lo * 3 : -lo * 3) - 2, oy + 44, 4, 3, outline);
    }
  } else {
    r(cx - 9, oy + 42, 18, 5, robeDark); // folded legs
  }
  // body
  const bodyTop = top + 14;
  const bodyH = pose.sitting ? 16 : 20;
  r(cx - 9, bodyTop, 18, bodyH, outline);
  r(cx - 8, bodyTop + 1, 16, bodyH - 2, robe);
  // sash / accent
  r(cx - 8, bodyTop + Math.floor(bodyH / 2), 16, 2, accent);
  if (facing === 'up') r(cx - 2, bodyTop + 1, 4, bodyH - 2, robeDark); // back seam
  // arms
  if (pose.armsUp) { r(cx - 12, bodyTop - 4, 3, 10, skin); r(cx + 9, bodyTop - 4, 3, 10, skin); }
  else if (facing === 'left') r(cx - 2, bodyTop + 3, 3, 9, robeDark);
  else if (facing === 'right') r(cx - 1, bodyTop + 3, 3, 9, robeDark);
  else { r(cx - 11, bodyTop + 2, 3, 10, robe); r(cx + 8, bodyTop + 2, 3, 10, robe); }
  if (pose.writing >= 0) { // pen hand forward
    r(cx + 3, bodyTop + 6 + pose.writing, 8, 2, skin);
    r(cx + 10, bodyTop + 4 + pose.writing, 1, 4, outline);
  }
  // head
  r(cx - 7, top, 14, 14, outline);
  r(cx - 6, top + 1, 12, 12, skin);
  // hair / face per facing (distinct silhouettes)
  if (facing === 'down') {
    r(cx - 6, top + 1, 12, 3, hair);
    r(cx - 6, top + 1, 2, 5, hair); r(cx + 4, top + 1, 2, 5, hair);
    r(cx - 4, top + 6, 2, 2, outline); r(cx + 2, top + 6, 2, 2, outline); // eyes
    r(cx - 1, top + 10, 3, pose.mouthOpen ? 2 : 1, outline);
  } else if (facing === 'up') {
    r(cx - 6, top + 1, 12, 12, hair);
    r(cx - 3, top - 2, 6, 4, hair); // bun
    r(cx - 3, top - 3, 6, 1, outline);
  } else {
    const s = facing === 'right' ? 1 : -1;
    r(cx - 6, top + 1, 12, 3, hair);
    r(cx - 6 * s - (s > 0 ? 0 : 0), top + 1, 12, 12, 'rgba(0,0,0,0)');
    // hair covers the back half of the head
    r(s > 0 ? cx - 6 : cx, top + 1, 6, 12, hair);
    r(cx - 3, top - 2, 6, 3, hair); // bun/tie
    r(cx + 3 * s - 1, top + 6, 2, 2, outline); // one eye
    r(cx + 6 * s - (s > 0 ? 0 : 1), top + 8, 1, 2, skin); // nose bump
    r(cx + 6 * s - (s > 0 ? -1 : 2), top + 8, 1, 2, outline);
    if (pose.mouthOpen) r(cx + 4 * s - (s > 0 ? 0 : 1), top + 11, 1, 1, outline);
  }
  // label on chest (front/back) or shoulder (side)
  const label = pal.label.slice(0, 3);
  const lw = label.length * 4 - 1;
  drawLabel(ctx, label, cx - Math.floor(lw / 2), bodyTop + 3, facing === 'up' ? hex(darken(pal.robe, 0.3)) : '#ffffff');
}

/**
 * Ensures the actor texture exists; when missing, generates the dev placeholder sheet with named
 * frames `<state>-<dir>-<n>` on a CanvasTexture. Returns the texture key.
 */
/** Texture key of the procedural placeholder for an actor (distinct from `ActorDef.texture`). */
export function devProcTextureKey(actorKey: string): string { return `dev-proc-${actorKey}`; }

export function ensureDevActorTexture(scene: Phaser.Scene, actorKey: string): string {
  const def = getActorDef(actorKey);
  const key = devProcTextureKey(actorKey);
  if (scene.textures.exists(key)) return key;
  const pal = def.dev ?? { robe: 0x777777, accent: 0x444444, skin: 0xcfcfcf, hair: 0x222222, label: '?' };
  const cols = ACTOR_STATES.reduce((a, s) => a + (DEV_FRAMES[s] ?? 0), 0);
  const canvas = document.createElement('canvas');
  canvas.width = cols * DEV_FRAME_W;
  canvas.height = FACINGS.length * DEV_FRAME_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) { console.warn('[characters] 2D context unavailable; dev texture skipped'); return key; }
  ctx.imageSmoothingEnabled = false;
  const frames: { name: string; x: number; y: number }[] = [];
  FACINGS.forEach((facing, row) => {
    let col = 0;
    for (const state of ACTOR_STATES) {
      for (let n = 0; n < (DEV_FRAMES[state] ?? 0); n++) {
        const ox = col * DEV_FRAME_W, oy = row * DEV_FRAME_H;
        drawCharacter(ctx, ox, oy, facing, poseFor(state, n), pal);
        frames.push({ name: frameName(state, facing, n), x: ox, y: oy });
        col++;
      }
    }
  });
  const tex = scene.textures.addCanvas(key, canvas);
  if (!tex) { console.warn(`[characters] could not register dev texture ${key}`); return key; }
  for (const f of frames) tex.add(f.name, 0, f.x, f.y, DEV_FRAME_W, DEV_FRAME_H);
  return key;
}

/** Icons the dev generator draws = the frames of the real `ui-emote-bubbles` atlas (same names, so a scene built against dev bubbles needs no change). */
export const DEV_EMOTES: readonly string[] = EMOTE_ICONS;

/** Content emote name → icon; the same table the real atlas is read through (`EMOTE_ALIAS`, emotes.ts). */
export const DEV_EMOTE_ALIAS: Readonly<Record<string, string>> = EMOTE_ALIAS;

function drawEmote(ctx: CanvasRenderingContext2D, rawName: string): void {
  const name: string = EMOTE_ALIAS[rawName] ?? rawName;
  const r = (x: number, y: number, w: number, h: number, c: string) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
  r(1, 1, 14, 14, '#2a1c14'); r(2, 2, 12, 12, '#fff4d6'); // bubble
  switch (name) {
    case 'exclaim': r(7, 4, 2, 6, '#8c2a22'); r(7, 11, 2, 2, '#8c2a22'); break;
    case 'question': drawLabel(ctx, '?', 7, 4, '#2b3a5c'); r(7, 9, 3, 1, '#fff4d6'); r(7, 4, 3, 5, '#fff4d6'); r(6, 4, 4, 1, '#2b3a5c'); r(9, 5, 1, 2, '#2b3a5c'); r(7, 7, 2, 1, '#2b3a5c'); r(7, 8, 1, 1, '#2b3a5c'); r(7, 11, 1, 1, '#2b3a5c'); break;
    case 'note': r(9, 4, 1, 6, '#2b3a5c'); r(9, 4, 3, 1, '#2b3a5c'); r(7, 9, 3, 2, '#2b3a5c'); break;
    case 'dots': r(4, 8, 2, 2, '#3a3f57'); r(7, 8, 2, 2, '#3a3f57'); r(10, 8, 2, 2, '#3a3f57'); break;
    case 'heart': r(4, 5, 3, 2, '#8c2a22'); r(9, 5, 3, 2, '#8c2a22'); r(3, 6, 10, 3, '#8c2a22'); r(5, 9, 6, 1, '#8c2a22'); r(6, 10, 4, 1, '#8c2a22'); r(7, 11, 2, 1, '#8c2a22'); break;
    case 'anger': r(4, 4, 3, 1, '#8c2a22'); r(9, 4, 3, 1, '#8c2a22'); r(4, 9, 3, 1, '#8c2a22'); r(9, 9, 3, 1, '#8c2a22'); r(4, 4, 1, 3, '#8c2a22'); r(11, 4, 1, 3, '#8c2a22'); r(4, 7, 1, 3, '#8c2a22'); r(11, 7, 1, 3, '#8c2a22'); break;
    case 'sweat': r(7, 4, 2, 2, '#1f8f8a'); r(6, 6, 4, 3, '#1f8f8a'); r(7, 9, 2, 2, '#1f8f8a'); r(7, 6, 1, 2, '#6fd3c8'); break;
    case 'bulb': r(6, 3, 4, 1, '#ffb347'); r(5, 4, 6, 4, '#ffb347'); r(6, 8, 4, 2, '#ffb347'); r(7, 10, 2, 2, '#6b6b73'); r(3, 5, 1, 1, '#ffb347'); r(12, 5, 1, 1, '#ffb347'); break; // 灯泡
    default: r(6, 6, 4, 4, '#3a3f57');
  }
}

/** Texture key of the DEV emote bubble for a content name (never `emote-<name>`: that key stays free for real art). */
export function devEmoteTextureKey(name: string): string { return `dev-emote-${name}`; }

/** Ensures the dev bubble `dev-emote-<name>` exists (drawn through `EMOTE_ALIAS`; unknown names get a plain bubble). Returns the key. */
export function ensureDevEmoteTexture(scene: Phaser.Scene, name: string): string {
  const key = devEmoteTextureKey(name);
  if (scene.textures.exists(key)) return key;
  const canvas = document.createElement('canvas');
  canvas.width = 16; canvas.height = 16;
  const ctx = canvas.getContext('2d');
  if (ctx) { ctx.imageSmoothingEnabled = false; drawEmote(ctx, name); }
  scene.textures.addCanvas(key, canvas);
  return key;
}

/** Showcase-only 32x32 floor/wall tiles. */
export function ensureDevTileTextures(scene: Phaser.Scene): { floor: string; wall: string } {
  const out = { floor: 'dev-tile-floor', wall: 'dev-tile-wall' };
  const make = (key: string, draw: (ctx: CanvasRenderingContext2D) => void) => {
    if (scene.textures.exists(key)) return;
    const canvas = document.createElement('canvas'); canvas.width = 32; canvas.height = 32;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    draw(ctx); scene.textures.addCanvas(key, canvas);
  };
  make(out.floor, (ctx) => {
    ctx.fillStyle = '#e2c48c'; ctx.fillRect(0, 0, 32, 32);
    ctx.fillStyle = '#d4b47c'; ctx.fillRect(0, 0, 32, 1); ctx.fillRect(0, 0, 1, 32);
    ctx.fillStyle = '#cfae74'; ctx.fillRect(9, 13, 2, 1); ctx.fillRect(21, 22, 2, 1); ctx.fillRect(15, 5, 1, 1);
  });
  make(out.wall, (ctx) => {
    ctx.fillStyle = '#8a5a32'; ctx.fillRect(0, 0, 32, 32);
    ctx.fillStyle = '#a06a3c'; ctx.fillRect(0, 0, 32, 12);
    ctx.fillStyle = '#5a3a1e'; ctx.fillRect(0, 12, 32, 1); ctx.fillRect(0, 31, 32, 1); ctx.fillRect(0, 0, 1, 32);
    ctx.fillStyle = '#6a4626'; ctx.fillRect(4, 18, 10, 6); ctx.fillRect(18, 18, 10, 6);
  });
  return out;
}
